/**
 * Where the sign-in token lives.
 *
 * sessionStorage, not localStorage, so closing the tab ends the session and the next
 * visit asks for a password. localStorage survives the browser being shut down and
 * restarted, which meant a session begun on Friday was still open on Monday on a
 * shared machine.
 *
 * This sits alongside the 30-minute idle timeout rather than replacing it: the timeout
 * covers someone who walks away and leaves the tab open, and this covers someone who
 * closes it and hands the machine on.
 *
 * One consequence worth knowing: sessionStorage belongs to a tab, not to the browser.
 * A link opened in a new tab from inside the app carries the session with it, because
 * browsers copy sessionStorage when a tab is duplicated; a tab opened from scratch and
 * typed into does not, and will ask for a password.
 *
 * Every call is wrapped because storage throws rather than returning null in a private
 * window or when site data is blocked, and a sign-in screen that crashes is worse than
 * one that simply asks again.
 */
const TOKEN = 'mrp_token';
const LAST_ACTIVITY = 'mrp_last_activity';

/* Older builds kept the token in localStorage. Anyone carrying one is signed out
 * once, deliberately: that is the behaviour being asked for, and leaving it behind
 * would keep exactly the sessions this is meant to end. */
try { localStorage.removeItem(TOKEN); localStorage.removeItem(LAST_ACTIVITY); } catch { /* blocked */ }

export function getToken() {
  try { return sessionStorage.getItem(TOKEN); } catch { return null; }
}

export function setToken(value) {
  try { sessionStorage.setItem(TOKEN, value); } catch { /* blocked */ }
}

export function clearSession() {
  try {
    sessionStorage.removeItem(TOKEN);
    sessionStorage.removeItem(LAST_ACTIVITY);
  } catch { /* blocked */ }
}

export function getLastActivity() {
  try {
    const v = parseInt(sessionStorage.getItem(LAST_ACTIVITY), 10);
    return Number.isFinite(v) ? v : null;
  } catch {
    return null;
  }
}

export function setLastActivity(t) {
  try { sessionStorage.setItem(LAST_ACTIVITY, String(t)); } catch { /* blocked */ }
}
